import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TOOLTIP_FADE_ANIMATION } from '../../../core/animations/TOOLTIP_FADE_ANIMATION';
import { cross } from '../../icons/cross';
import { provideIcons } from '../../icons/icon-provider';
import { IconComponent } from '../../icons/icon/icon.component';

@Component({
  selector: 'app-tooltip-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  providers: [provideIcons({ cross })],
  styleUrls: [],
  styles: [
    `
      :host {
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        background-color: black;
        color: white;
        opacity: 0.9;
        max-width: 300px;
        min-width: 250px;
        z-index: 10;
        text-align: center;
      }
      .tooltip-inner {
        padding: 0.75rem 1rem;
        font-size: 1rem;
      }
      app-icon[name='cross'] {
        position: relative;
        top: -0.75rem;
        right: -1rem;
        background-color: var(--primary-color);
        color: var(--secondary-text-color);
        padding: 5px;
        font-size: 13px;
        float: right;
        cursor: pointer;
        padding: 0.75rem;
      }
    `
  ],
  animations: [TOOLTIP_FADE_ANIMATION],
  template: `<div class="tooltip-inner">
    <app-icon name="cross" class="ml-2 d-block d-sm-none" (click)="closeMe.emit()" /><ng-content></ng-content>
  </div>`
})
export class tooltipComponent implements OnInit {
  @HostBinding('@fadeInOut') fadeInOutTrigger = 'in';
  @HostBinding('style.transform') @Input() transform: string;
  @Output() closeMe = new EventEmitter();
  constructor(private cdr: ChangeDetectorRef) {
    cdr.detach();
  }

  ngOnInit(): void {
    this.cdr.detectChanges();
  }
}

@Directive({
  selector: '[appTooltip]',

  exportAs: 'appTooltip'
})
export class alpiTooltipDirective implements OnInit, OnDestroy {
  private _tooltip: string | TemplateRef<unknown> = '';
  private tooltipRef: ComponentRef<unknown> | undefined;
  private viewRef: EmbeddedViewRef<unknown>;
  private closeSub: Subscription;

  @Input() set appTooltip(tooltip: string | TemplateRef<unknown>) {
    this._tooltip = tooltip;
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private applicationRef: ApplicationRef,
    private injector: Injector,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      this.elementRef.nativeElement.addEventListener('mouseenter', this.open.bind(this), { passive: true });
      this.elementRef.nativeElement.addEventListener('click', this.open.bind(this), { passive: true });
      this.elementRef.nativeElement.addEventListener('mouseleave', this.close.bind(this), { passive: true });
    });
  }

  open() {
    if (!this.tooltipRef) {
      this.tooltipRef = this.viewContainerRef.createComponent(tooltipComponent, {
        injector: this.injector,
        projectableNodes:
          typeof this._tooltip === 'string'
            ? [[this.renderer.createText(this._tooltip)]]
            : this.getProjectableContent(this._tooltip)
      });
      // get absolute position of hovered element to find out if popup is outside the viewport (product selection)
      // if so, then shift it to the right by half the popup's maximum width
      const pos = this.getDocumentOffsetPosition(this.elementRef.nativeElement);

      // position the tooltip centered below the element.
      // left: half of the tooltip to the left plus the hovered element offsetLeft (flexboxed elements)
      // top: offsetTop plus height of the hovered element plus 6px as gap to make it look nicer
      let offsetLeft = 0;
      if (pos.left < 150 && this.elementRef.nativeElement.clientWidth < 250) {
        offsetLeft = this.elementRef.nativeElement.offsetLeft + 150;
      } else if (pos.width >= 600) {
        offsetLeft = this.elementRef.nativeElement.offsetLeft + this.elementRef.nativeElement.clientWidth / 2;
      }

      this.tooltipRef.setInput(
        'transform',
        `translate(calc(-50% + ${offsetLeft}px), ${6 + this.elementRef.nativeElement.offsetTop + this.elementRef.nativeElement.clientHeight}px)`
      );
      this.cdr.detectChanges();
      // hook up the close button for mobile tooltips since closing from within the component is kinda not great to do
      this.closeSub = (this.tooltipRef.instance as tooltipComponent).closeMe.subscribe(() => {
        this.close();
      });
      this.applicationRef.tick(); // apply style changes and let angular refresh the DOM, no matter if by string or ng-template
    }
  }

  private getProjectableContent(content: TemplateRef<unknown>): Node[][] {
    this.viewRef = content.createEmbeddedView({ injector: this.injector });
    this.applicationRef.attachView(this.viewRef);
    return [this.viewRef.rootNodes];
  }

  /**
   * @param el the HTMLElement you want to know the position of
   * @returns top, left of the position
   */
  private getDocumentOffsetPosition(el: HTMLElement): { top: number; left: number; width: number } {
    let top = 0,
      left = 0,
      width = 0;
    while (el !== null) {
      top += el.offsetTop;
      left += el.offsetLeft;
      width = el.clientWidth;
      el = el.offsetParent as HTMLElement;
    }
    return { top, left, width };
  }

  close() {
    if (this.tooltipRef) {
      this.viewContainerRef.remove(this.viewContainerRef.indexOf(this.tooltipRef.hostView));
      this.tooltipRef = null;
      setTimeout(() => {
        this.viewRef?.destroy();
      }, 100);
      this.cdr.detectChanges();
    }
    this.closeSub?.unsubscribe();
  }

  ngOnDestroy() {
    this.close();
  }
}
